import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HiPlus, HiSearch } from 'react-icons/hi';
import { Button, Input, Select, Card, CardHeader, Table, Badge, Pagination, Modal } from '../components/ui';
import { listLicenses, createLicense, getCustomerLicenses } from '../services/licenses.api';
import { listCustomers } from '../services/customers.api';
import { getActiveProducts } from '../services/products.api';
import { useAuthStore } from '../store/authStore';
import { formatDate, getRuntimeLabel } from '../utils/helpers';
import { RUNTIME_OPTIONS, LICENSE_STATUS_OPTIONS } from '../utils/constants';
import { getErrorMessage } from '../services/api';
import type { License, LicenseStatus, Runtime } from '../types';

export function Licenses() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isCustomer = user?.role === 'CUSTOMER';
  const canCreate = user?.role === 'ADMIN' || user?.role === 'OWNER' || user?.role === 'INVOICE_TEAM';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LicenseStatus | ''>('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // Create form state
  const [customerId, setCustomerId] = useState('');
  const [productId, setProductId] = useState('');
  const [deviceCount, setDeviceCount] = useState(1);
  const [runtime, setRuntime] = useState<Runtime>('ONE_YEAR');
  const [hardwareId, setHardwareId] = useState('');

  // For admin/invoice: use listLicenses with pagination
  const { data, isLoading } = useQuery({
    queryKey: ['licenses', { page, search, status: statusFilter }],
    queryFn: () => listLicenses({ page, search, status: statusFilter || undefined }),
    enabled: !isCustomer,
  });

  // For customers: use getCustomerLicenses
  const { data: customerLicenses, isLoading: customerLoading } = useQuery({
    queryKey: ['customerLicenses', user?.customerId],
    queryFn: () => getCustomerLicenses(user!.customerId!),
    enabled: isCustomer && !!user?.customerId,
  });

  // Combine data for display
  const licenses = isCustomer ? customerLicenses : data?.data;
  const loading = isCustomer ? customerLoading : isLoading;

  const { data: customers } = useQuery({
    queryKey: ['customers', 'dropdown'],
    queryFn: () => listCustomers({ limit: 100 }),
    enabled: isCreateOpen,
  });

  const { data: products } = useQuery({
    queryKey: ['products', 'active'],
    queryFn: getActiveProducts,
    enabled: isCreateOpen,
  });

  const createMutation = useMutation({
    mutationFn: () => createLicense({ customerId, productId, deviceCount, runtime, hardwareId: hardwareId.trim() || undefined }),
    onSuccess: () => {
      toast.success('License created successfully');
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const resetForm = () => {
    setCustomerId('');
    setProductId('');
    setDeviceCount(1);
    setRuntime('ONE_YEAR');
    setHardwareId('');
  };

  const columns = [
    {
      key: 'licenseKey',
      header: 'License Key',
      render: (license: License) => (
        <span className="font-mono text-sm">{license.licenseKey.slice(0, 8)}...</span>
      ),
    },
    // Only show customer column for non-customer users
    ...(!isCustomer ? [{
      key: 'customer',
      header: 'Customer',
      render: (license: License) => license.customer?.name || '-',
    }] : []),
    {
      key: 'product',
      header: 'Product',
      render: (license: License) => license.product?.name || '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (license: License) => <Badge status={license.status}>{license.status}</Badge>,
    },
    {
      key: 'devices',
      header: 'Devices',
      render: (license: License) => `${license._count?.installations ?? 0} / ${license.deviceCount}`,
    },
    {
      key: 'runtime',
      header: 'Runtime',
      render: (license: License) => getRuntimeLabel(license.runtime),
    },
    {
      key: 'expiresAt',
      header: 'Expires',
      render: (license: License) => formatDate(license.expiresAt),
    },
  ];

  return (
    <div>
      <CardHeader
        title="Licenses"
        description="Manage software licenses"
        action={
          canCreate && (
            <Button onClick={() => setIsCreateOpen(true)}>
              <HiPlus className="w-4 h-4 mr-2" />
              New License
            </Button>
          )
        }
      />

      <Card className="mb-6" padding={false}>
        {!isCustomer && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Search licenses..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select
                options={[{ value: '', label: 'All Statuses' }, ...LICENSE_STATUS_OPTIONS]}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as LicenseStatus | '')}
                className="w-48"
              />
            </div>
          </div>
        )}

        <Table
          columns={columns}
          data={licenses || []}
          keyExtractor={(license) => license.id}
          onRowClick={(license) => navigate(`/licenses/${license.id}`)}
          isLoading={loading}
          emptyMessage="No licenses found"
        />

        {!isCustomer && data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            total={data.total}
            limit={data.limit}
            onPageChange={setPage}
          />
        )}
      </Card>

      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create New License"
        size="lg"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="space-y-4"
        >
          <Select
            label="Customer"
            options={customers?.data.map((c) => ({ value: c.id, label: c.name })) || []}
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            placeholder="Select customer"
            required
          />
          <Select
            label="Product"
            options={products?.map((p) => ({ value: p.id, label: p.name })) || []}
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            placeholder="Select product"
            required
          />
          <Input
            type="number"
            label="Device Count"
            value={deviceCount}
            onChange={(e) => setDeviceCount(parseInt(e.target.value))}
            min={1}
            required
          />
          <Select
            label="Runtime"
            options={[...RUNTIME_OPTIONS]}
            value={runtime}
            onChange={(e) => setRuntime(e.target.value as Runtime)}
            required
          />
          <Input
            type="text"
            label="Hardware-ID (optional)"
            value={hardwareId}
            onChange={(e) => setHardwareId(e.target.value)}
            placeholder="z. B. 35840662ca045fe8 — aus der WiFi-Tracker-App (Admin → Lizenz → Diese Maschine)"
          />
          <p className="-mt-2 text-xs text-gray-500">
            Wird die Hardware-ID gesetzt, wird sofort eine signierte Offline-Lizenz erzeugt. Kann auch später nachgetragen werden.
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              Create License
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
